import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Tag input class recipes.
 *
 * Zero `dark:` variants and zero slots of its own, the same shape as `MultiSelect.recipe`. Ported
 * from the Figma `Tag Input` set (file 5aSkE0U4TeWbuIZDvccHjc, node 1472:151191, read 2026-09-25 —
 * 64 cells over State x Position x isFilled x isDisabled x hasError x isOpen).
 *
 * The set's invariant is that its axes "match Text Input exactly", so a Tag Input and a Text Input
 * line up in a stack. That is made true by construction: the field IS the text input's box, label and
 * helper, the menu IS the select's panel and rows, and each tag IS a `Tag` Sm Neutral:
 *
 *   textInputRootRecipe / textInputBoxRecipe / textInputLabelRecipe / textInputHelperRecipe
 *   textInputTrailingButtonRecipe   the clear ✕ (Figma's `rightButton`)
 *   selectPositionerRecipe / selectMenuRecipe / selectListRecipe / selectItemRecipe
 *   tagRecipe / tagContentRecipe / tagLabelRecipe / tagRemoveRecipe
 *   multiSelectOverflowRecipe       the "+N more" remainder, shared so the two read identically
 *
 * What is left here is only what a tag input has and its siblings do not: a row that holds tags and
 * the text control together, and the one line of copy for an empty result.
 *
 * ── The state reading ─────────────────────────────────────────────────────────────────────────
 *
 * Inherited from `textInputBoxRecipe`, and it is the set's own: Active is "holds the caret" and draws
 * the 2px accent edge from a click or a Tab; Focus is the keyboard-only ring, which the whole field
 * family leaves undrawn until input-modality tracking exists.
 *
 * ONE DELIBERATE DIFFERENCE from the drawing: the list opening does NOT switch the field to Select's
 * engaged look. The set's open cells are drawn at Default, and its own edge case 7 leaves the Active +
 * open combination for the port to decide. An open Tag Input always holds the caret — the list opens
 * because someone is typing — so it keeps the Active edge. `data-open` is therefore never written on
 * the field root; the `data-popup-open` Base UI puts on the input is enough for anything that needs it.
 */

/**
 * The row that holds the tags, the "+N more" remainder and the `<input>` — Base UI's `Combobox.Chips`.
 *
 * It pays ALL the padding, as the text input's control does, so the whole box above and below the
 * tags is the row's and a press anywhere lands in it. `forwardBoxPress` on the box hands the rest
 * (the helper line) to the input.
 *
 * Geometry, offsets from the OUTER edge, 1px border included (Figma 1472:151212, 1472:151240):
 *
 *                     label     row        below      height
 *   no tags           as TextInput: value line 30–50              60, 82 with helper
 *   tags              8–28      28–52      8          60
 *   tags + helper     7–27      27–51      helper     82          (helper 55–75, then 7)
 *
 * The tags cells take a pixel off each side because Tag Sm is 24px in the family's 20px value line.
 * The helper row follows the drawn ERROR cell (82px) rather than the set's boolean probe, which measures
 * 84 with a neutral helper — the description lists that 2px step as a slip, and one height for both
 * roles of the same line is what the field family does everywhere else.
 *
 * AT REST it holds one line and clips; while editing it wraps. `data-editing` is written by the
 * component from focus-within, not from `:focus-within`, because the SAME flag decides in JS how many
 * tags to draw before the remainder — see `TagInput.tsx`. One source keeps the two from disagreeing.
 */
export const tagInputRowRecipe = cva(
  [
    'relative flex w-full items-center gap-[4px]',
    'flex-nowrap overflow-hidden',
    'group-data-[editing]/ti:flex-wrap',
    /**
     * While editing, the wrapped tags stop at 280px and scroll, so a category holding dozens of tags
     * does not push the page down. Not drawn: the set shows editing with a handful of tags only. 280px
     * is the legacy MUI field's own cap, carried over until design names a height.
     */
    'group-data-[editing]/ti:max-h-[280px] group-data-[editing]/ti:overflow-y-auto',
    'cursor-text group-data-[disabled]/ti:cursor-not-allowed',
  ],
  {
    variants: {
      /** No classes of its own: the vertical inset depends on BOTH of these at once. */
      hasTags: { true: 'min-h-[24px]', false: 'min-h-[20px]' },
      hasHelper: { true: '', false: '' },
      /** 24px gutter; 24 + the 24px clear button + its 4px gap when `rightButton` is on. */
      hasClear: { true: 'pr-[52px] pl-[24px]', false: 'px-[24px]' },
    },
    compoundVariants: [
      // The empty field is the text input's own control box: value line at 30, 60px.
      { hasTags: false, hasHelper: false, class: 'pt-[29px] pb-[9px]' },
      { hasTags: false, hasHelper: true, class: 'pt-[29px] pb-0' },
      { hasTags: true, hasHelper: false, class: 'pt-[27px] pb-[7px]' },
      { hasTags: true, hasHelper: true, class: 'pt-[26px] pb-0' },
    ],
    defaultVariants: { hasTags: false, hasHelper: false, hasClear: false },
  }
);

/**
 * With tags and helper text the drawn cell puts 6px under the helper, against the family's 7.
 * `cn()` is `tailwind-merge`, so this REPLACES `textInputBoxRecipe`'s `pb-[7px]`.
 */
export const tagInputBoxRecipe = cva([], {
  variants: {
    hasTags: { true: '', false: '' },
    hasHelper: { true: '', false: '' },
  },
  compoundVariants: [{ hasTags: true, hasHelper: true, class: 'pb-[6px]' }],
  defaultVariants: { hasTags: false, hasHelper: false },
});

/**
 * The floating label, raised to where the tags cells draw it: 8px from the outer edge, 7 with a helper,
 * against the family's 10 — the same lift `multiSelectBadgesLabelRecipe` makes.
 *
 * Floated UNCONDITIONALLY once a tag lands, rather than only on `data-filled`: Base UI's `Field` reads
 * "filled" off the text control, and a field holding three tags with nothing typed is filled all the
 * same. The base `translate`/`scale` and both group variants are restated so `tailwind-merge` replaces
 * each of `textInputLabelRecipe`'s by its own modifier rather than racing it in the stylesheet.
 */
export const tagInputLabelRecipe = cva([], {
  variants: {
    hasTags: { true: '', false: '' },
    hasHelper: { true: '', false: '' },
  },
  compoundVariants: [
    {
      hasTags: true,
      hasHelper: false,
      class:
        'translate-y-[7px] scale-[0.857143] group-data-[filled]/ti:translate-y-[7px] group-data-[focused]/ti:translate-y-[7px]',
    },
    {
      hasTags: true,
      hasHelper: true,
      class:
        'translate-y-[6px] scale-[0.857143] group-data-[filled]/ti:translate-y-[6px] group-data-[focused]/ti:translate-y-[6px]',
    },
  ],
  defaultVariants: { hasTags: false, hasHelper: false },
});

/**
 * A tag — the `Tag` Sm Neutral pill, applied to Base UI's `Combobox.Chip` rather than rendered as the
 * `Tag` component.
 *
 * Not the component, because the set and the shipped `Tag` disagree about the ✕ and the set is right
 * for this context. `Tag` renders its remove control as a focusable button; the set requires it to be
 * pointer-only, so the field keeps ONE tab stop and Backspace is the keyboard route. `Combobox.Chip`
 * gives exactly that — the chips are reachable with ArrowLeft/ArrowRight from the input, never by Tab
 * — so the pill's classes are reused and the behaviour comes from Base UI.
 *
 * `interactive` is `tagRecipe`'s hover, which the set asks for per tag: "hovering a tag switches THAT
 * tag to Tag State=Hover; the field's own State=Hover is separate".
 *
 * A chip reached by the arrow keys takes the shared ring. The set draws none — its Tag Focus variant
 * "has no consumer here" — but a focused element must show where it is (WCAG 2.4.7), and it is only
 * ever keyboard-reached, so `:focus-visible` is exact here in a way it is not on the text control.
 *
 * `min-w-0` lets the first tag truncate when even it does not fit a narrow field at rest.
 *
 * Disabled dims to 60% rather than restyling, per the set: "Tag has no disabled state… the row-level
 * opacity is the treatment". Written on each chip because the chips share their row with the input.
 */
export const tagInputChipRecipe = cva([
  'min-w-0 outline-none',
  focusRing('self'),
  'group-data-[disabled]/ti:opacity-60',
]);

/**
 * The remainder, dimmed with the tags it counts. `multiSelectOverflowRecipe` supplies the type and
 * colour.
 */
export const tagInputOverflowRecipe = cva(['group-data-[disabled]/ti:opacity-60']);

/**
 * The text control, inline after the tags.
 *
 * With no tags it is the whole value line, as on `TextInput`. With tags, at rest it collapses to zero
 * width so the tags and the remainder own the line — it is still focusable, which is what lets Tab and
 * a press on the field reach it — and while editing it takes the rest of the last line, wrapping onto
 * a new one below 64px, the width of about six typed characters.
 *
 * 24px tall beside tags so the caret centres on the pill (Figma: caret 16px at y=32, text at 30).
 */
export const tagInputControlRecipe = cva(
  [
    'm-0 block appearance-none border-0 bg-transparent p-0',
    'font-sans text-body-md text-[var(--ds-input-value)]',
    'outline-none',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-input-value-disabled)]',
  ],
  {
    variants: {
      hasTags: {
        true: 'h-[24px] w-0 min-w-0 flex-none group-data-[editing]/ti:min-w-[64px] group-data-[editing]/ti:flex-1',
        false: 'h-[20px] min-w-0 flex-1',
      },
    },
    defaultVariants: { hasTags: false },
  }
);

/**
 * The one line an empty result shows — Figma's edge case 3, "No results found".
 *
 * The set borrows a `List-Item State=Disabled` for it, and its own Known gaps note that the borrowed
 * row fails contrast in light. It is a sentence the person must read, so it takes the secondary
 * foreground instead (`--ds-select-item-icon`, `foreground/secondary`) — above AA in both themes, and
 * still visibly not a choice. Same box as a row so the panel does not change size when it appears.
 */
export const tagInputEmptyRecipe = cva([
  'px-[12px] py-[8px]',
  'font-sans text-label-lg select-none',
  'text-[var(--ds-select-item-icon)]',
]);
